import { Box, FormControl, FormGroup } from '@mui/material';
import { FormikContextType } from 'formik';
import { CheckboxComponent } from '../../../components/inputComponents/CheckboxComponent';
import { InputNumberSpinner } from '../../../components/inputComponents/InputNumberSpinner';
import { limitsStateValues } from '../../../types/componentTypes/limits';
import { TierLimits } from './TierLimits';
import { CalendarCycleLimit } from '../../../components/assignPromotionFlowComponent/LimitTypes/CalendarCycleLimit';
import TooltipText from '../../../components/inputComponents/TooltipText';

interface props {
    formik: FormikContextType<limitsStateValues>;
    tiersList: number[];
    promoFlow: string;
    isInstantWin: boolean;
    isCnG: boolean;
}

const WinningLimits = ({ formik, tiersList, promoFlow, isCnG, isInstantWin }: props) => {
    const tierLimitsField = isCnG ? 'redemptionLimitsPerTier' : 'winningLimitsPerTier';

    const handlePerTierChecker = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        if (!checked) {
            delete formik.values.flow?.[promoFlow]?.params?.[tierLimitsField];
        } else {
            if (!formik.values.flow?.[promoFlow]?.params?.[tierLimitsField]) {
                formik.setFieldValue(`flow.${promoFlow}.params`, {
                    ...formik.values.flow?.[promoFlow]?.params,
                    [tierLimitsField]: {},
                });
            }
        }
    };

    return (
        <Box>
            <FormControl className='form-control-participation-limits'>
                {isInstantWin && (
                    <>
                        <FormGroup>
                            <CheckboxComponent
                                fieldName={'tempParams.instantWinLimitPerConfiguration'}
                                label='Lifetime'
                            />
                            {formik.values.tempParams.instantWinLimitPerConfiguration && (
                                <InputNumberSpinner
                                    fieldName={'tempParams.instantWinLimitPerConfiguration'}
                                    label={'Limit'}
                                    incrementValue={1}
                                    minValue={1}
                                />
                            )}
                        </FormGroup>
                        <FormGroup>
                            <CheckboxComponent
                                fieldName={'tempParams.instantWinLimitPerHourFlag'}
                                label='Per Hour'
                            />
                            {formik.values.tempParams.instantWinLimitPerHourFlag && (
                                <>
                                    <InputNumberSpinner
                                        fieldName={'tempParams.instantWinLimitPerTimeInterval'}
                                        label={'Limit'}
                                        incrementValue={1}
                                        minValue={1}
                                    />
                                    <InputNumberSpinner
                                        fieldName={'tempParams.instantWinLimitPerTimeIntervalValue'}
                                        label={'Limit window in hours'}
                                        incrementValue={1}
                                        minValue={1}
                                    />
                                </>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <CheckboxComponent
                                fieldName={'tempParams.instantWinCalendarCycleLimitFlag'}
                                testingId='winning-calendar-cycle-limit-checkbox'
                                label={
                                    <TooltipText
                                        dataTestId='winning-calendar-cycle-limit-tooltip'
                                        text={'Calendar (week / month / year)'}
                                        textVariant='body1'
                                        tooltip='Calendar-based winning limits allow users to control how many times a consumer can win within a defined calendar period. Limits reset automatically based on the selected calendar cycle (weekly, monthly, or yearly).'
                                    />
                                }
                            />
                            {formik.values.tempParams.instantWinCalendarCycleLimitFlag &&
                                <CalendarCycleLimit
                                    formik={formik}
                                    limitsField='winningLimits.instantWinCalendarCycleLimit'
                                />
                            }
                        </FormGroup>
                    </>
                )}
                <FormGroup>
                    <CheckboxComponent
                        fieldName={`tempParams.${tierLimitsField}Flag`}
                        label='Per Prize Tier'
                        customAction={handlePerTierChecker}
                    />
                    {(formik.values.tempParams.winningLimitsPerTierFlag || formik.values.tempParams.redemptionLimitsPerTierFlag) && (
                        <TierLimits
                            formik={formik}
                            tiersList={tiersList}
                            promoFlow={promoFlow}
                            tierLimitsField={tierLimitsField}
                        />
                    )}
                </FormGroup>
            </FormControl>
        </Box>
    );
};

export { WinningLimits };
